import axios from 'axios';
import { polusBaseUrl, POST } from '../../../../../../utils/axios-interceptor';
import { getTimeStamp } from '../../../../../../utils/dateUtils';

// Metrics requested from the AICA financial-metrics endpoint. Order here does not
// drive the UI — the UI order comes from the metric config in constants.ts.
const FINANCIAL_METRICS_REQUEST = {
    TYPE: 'auditedMissingProvisionals',
    START_DATE: '2019-04-01',
    FREQUENCY: 'Yearly',
    TASK_STEP: 'AION_AUDITED_FINANCIAL',
    MIS: 'Balance Sheet',
    CLASSIFICATION_ID: 30,
} as const;

const METRIC_LIST = [
    'Revenue',
    'RevenueGrowthPercentage',
    'EbitaMargin',
    'PAT',
    'CashAndCashEquivalent',
    'TotalCurrentAssets',
    'TotalCurrentLiabilities',
    'NetWorkingCapital',
    'LongTermDebt',
    'ShortTermDebt',
    'TangibleNetWorth',
];

export interface FinancialMetricsHeader {
    name: string;
    key: string;
    type?: string;
}

export interface FinancialMetricsRow {
    metricName: string;
    unit: string;
    definitionText?: string;
    [yearKey: string]: string | number | null | undefined;
}

export interface FinancialMetricsData {
    headers: FinancialMetricsHeader[];
    rows: FinancialMetricsRow[];
    latestConsolidatedFY: number;
    latestStandaloneFY: number;
}

// Mirrors the working request for getAicaFinancialMetrics. Auth headers are
// injected by the shared axios interceptor; we only override the viewed-company
// org id so the endpoint resolves financials for the company on screen.
export async function fetchAicaFinancialMetrics(orgId: string): Promise<FinancialMetricsData | null> {
    const response = await axios({
        baseURL: polusBaseUrl,
        url: 'unifier/getAicaFinancialMetrics',
        method: POST,
        headers: { 'x-organization-id': orgId },
        data: {
            requestPayload: {
                requestId: getTimeStamp(),
                orgId,
                type: FINANCIAL_METRICS_REQUEST.TYPE,
                startDate: FINANCIAL_METRICS_REQUEST.START_DATE,
                endDate: null,
                frequency: FINANCIAL_METRICS_REQUEST.FREQUENCY,
                metricList: METRIC_LIST,
                taskStepsList: [FINANCIAL_METRICS_REQUEST.TASK_STEP],
                mis: FINANCIAL_METRICS_REQUEST.MIS,
                raw: true,
                classificationId: FINANCIAL_METRICS_REQUEST.CLASSIFICATION_ID,
                round: true,
            },
        },
    });

    return response?.data?.responseBody?.UW_FETCH_FINANCIAL_METRICS?.data?.data ?? null;
}
